import React from 'react';
import clsx from 'clsx';
import { IPicture, Picture } from '../Image';
import { Image } from '../Image';
import ImageBox from '../ImageBox';

import {
  Content,
  Body,
  Footer,
  Card,
  Color,
  ColorCount,
  ColorList,
  ProductImage,
  Text,
  DescriptionText,
  Variants,
  VariantText,
  PriceCommunicationText,
  Markings,
  LeftMarking,
  RightMarking,
  MarkingWrapper,
  SecondaryImage,
  PrimaryImage,
  LeftColumn,
  RightColumn,
  TestfreaksItemWrapper,
} from './wrapper';
import ProductPrices from '../../components-legacy/ProductPrices';
import { TestFreaksItem } from '../../components-legacy/Review';

interface IColor {
  hex: string;
  name: string;
}

export interface IProductCard {
  brandImage?: string;
  campaignSlot?: React.ReactNode;
  colors: IColor[];
  description: string;
  energyBadge?: string;
  groupId: string;
  moderArtikelId?: string;
  horizontalProductCard?: boolean;
  href: string;
  image: IPicture;
  imageLoading?: 'eager' | 'lazy';
  onClick?: () => void;
  onMouseEnterImage?: () => void;
  onMouseLeaveImage?: () => void;
  overflowSlot?: React.ReactNode;
  price: IPrice;
  priceCommunication: TPriceCommunication | null;
  priceDesign: TPriceDesign | null;
  primarySlot?: React.ReactNode;
  secondaryImage?: IPicture;
  secondarySlot?: React.ReactNode;
  size?: 'small' | 'large';
  title: string;
  showSecondaryImage?: boolean;
  variantCount?: number | string;
}

const ProductCard = (
  {
    brandImage,
    campaignSlot = null,
    colors,
    description,
    energyBadge,
    groupId,
    moderArtikelId,
    horizontalProductCard,
    href,
    image,
    imageLoading = 'lazy',
    onClick,
    onMouseEnterImage,
    onMouseLeaveImage,
    overflowSlot = null,
    price,
    priceCommunication,
    priceDesign,
    primarySlot = null,
    secondaryImage,
    secondarySlot = null,
    size = 'small',
    title,
    showSecondaryImage,
    variantCount,
  }: IProductCard,
  ref: React.ForwardedRef<HTMLAnchorElement>,
) => {
  const hasMarkings = primarySlot || secondarySlot || overflowSlot;
  const isSmall = size === 'small';

  return (
    <Card href={href} onClick={onClick} ref={ref}>
      <ProductImage onMouseEnter={onMouseEnterImage} onMouseLeave={onMouseLeaveImage}>
        <PrimaryImage>
          <Picture sources={image.sources} image={image.image} imageLoading={imageLoading} />
        </PrimaryImage>

        {secondaryImage && (
          <SecondaryImage className={showSecondaryImage ? 'show' : undefined}>
            <Picture
              sources={secondaryImage.sources}
              image={secondaryImage.image}
              imageLoading={showSecondaryImage ? imageLoading : 'lazy'}
            />
          </SecondaryImage>
        )}

        {hasMarkings && (
          <Markings className={isSmall ? 'push-down-ltMd' : undefined}>
            <LeftMarking>{primarySlot}</LeftMarking>
            <RightMarking>
              <MarkingWrapper>
                {secondarySlot}
                {overflowSlot}
              </MarkingWrapper>
            </RightMarking>
          </Markings>
        )}

        {campaignSlot}
      </ProductImage>

      <Content
        className={clsx('slim', isSmall && 'narrow-ltSM', horizontalProductCard && 'horizontal')}>
        <Body>
          {(colors.length > 1 || variantCount) && (
            <Variants className="m-b-half">
              {colors.length > 1 && (
                <ColorList>
                  <>
                    {colors.slice(0, 4).map((color, i) => (
                      <Color key={`${color.name}-${i}`} title={color.name} color={color.hex} />
                    ))}

                    {colors.length > 4 && <ColorCount>+{colors.length - 4}</ColorCount>}
                  </>
                </ColorList>
              )}

              {!!variantCount && <VariantText>{variantCount} varianter</VariantText>}
            </Variants>
          )}

          <Text>{title}</Text>

          <DescriptionText>{description}</DescriptionText>
        </Body>

        {priceCommunication && (
          <PriceCommunicationText color={priceCommunication.textColor}>
            {priceCommunication.title}
          </PriceCommunicationText>
        )}

        <Footer className={`productCardFooter ${size}`}>
          <LeftColumn>
            <ProductPrices
              badge={
                energyBadge && (
                  <ImageBox width="30px" height="30px" breakpointSizes={{ LtSm: 24 }} align="left">
                    <Image src={energyBadge} />
                  </ImageBox>
                )
              }
              priceDesign={priceDesign}
              priceObject={{
                originalPrice: price.originalPrice,
                sellingPrice: price.price,
                referencePrice: price.referencePrice,
                pilPrice: price.pilPrice,
                componentPrice: price.componentPrice,
              }}
              hasPriceFlag={!!priceCommunication}
            />
            <TestfreaksItemWrapper>
              <TestFreaksItem {...{ moderArtikelId, groupId }} />
            </TestfreaksItemWrapper>
          </LeftColumn>

          <RightColumn>
            {brandImage && (
              <ImageBox
                width="72px"
                height="48px"
                breakpointSizes={{ LtSm: { width: 72, height: 32 } }}
                align="right"
                alignLtSm="right">
                <Image src={brandImage} />
              </ImageBox>
            )}
          </RightColumn>
        </Footer>
      </Content>
    </Card>
  );
};

export default React.forwardRef(ProductCard);
